import React, { useEffect, useRef } from "react";
import Layout from "../../components/layout/layout";
import {
  ExploreIcon,
  MailIcon,
  MapIcon,
  PhoneIcon,
} from "../../assets/images/svgExports";
import Button from "../../components/library/button";
import ContactImage from "../../assets/images/contact.png";

import gsap from "gsap";

const ContactPage = () => {
  const contactPageRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".contact-hero-card", {
        y: 40,
        opacity: 0,
        duration: 0.9,
        ease: "power3.out",
      });

      gsap.from(".contact-hero-image", {
        x: 40,
        opacity: 0,
        duration: 1,
        ease: "power3.out",
        delay: 0.15,
      });

      gsap.from(".contact-info-item", {
        y: 20,
        opacity: 0,
        duration: 0.7,
        ease: "power3.out",
        stagger: 0.08,
        delay: 0.25,
      });

      gsap.from(".contact-form-section", {
        y: 60,
        opacity: 0,
        duration: 1,
        ease: "power3.out",
        delay: 0.3,
      });
    }, contactPageRef);

    return () => ctx.revert();
  }, []);

  return (
    <Layout>
      <div ref={contactPageRef} className="h-full py-12 bg-white">
        <div className="max-w-6xl relative mx-auto px-6 py-20">
          <div className="relative mb-20">
            <div className="flex flex-col gap-10 lg:gap-0">
              <div className="contact-hero-card w-full lg:w-[650px] bg-sky-50 rounded-3xl p-6 sm:p-8 shadow-sm lg:pr-28">
                <button
                  className="
                      inline-flex items-center mb-6 justify-center
                      px-4 py-2
                      rounded-full
                      bg-[#C4DFFB]
                      text-[#2C73DB]
                      border border-white/80
                      text-xs
                      font-[300]
                      shadow-[0_0_18px_rgba(59,130,246,0.25)]
                      transition-shadow
                    "
                >
                  Get In Touch
                </button>
                <h1 className="text-3xl sm:text-4xl font-normal text-gray-900 mb-4">
                  Let's Build Something
                  <br />
                  <span className="text-[#2C73DB]">Amazing Together</span>
                </h1>

                <p className="text-[#8e8e8e] max-w-sm mb-8">
                  Ready to transform your customer engagement?
                  <br />
                  Our team is here to help you create meaningful connections
                  with your audience across Africa.
                </p>

                <div className="space-y-4 sm:space-y-6 max-w-sm w-full">
                  <div className="contact-info-item flex items-center w-full py-4 pl-4 sm:pl-6 gap-4 bg-white rounded-3xl">
                    <MailIcon />
                    <div>
                      <p className="text-sm text-gray-500 font-medium">
                        Email us
                      </p>
                      <p className="text-gray-900 font-medium break-all">
                        hello@bridgetune.com
                      </p>
                    </div>
                  </div>

                  <div className="contact-info-item flex items-center w-full py-4 pl-4 sm:pl-6 gap-4 bg-white rounded-3xl">
                    <PhoneIcon />
                    <div>
                      <p className="text-sm text-gray-500 font-medium">
                        Call us
                      </p>
                      <p className="text-gray-900 font-medium">
                        +234 800 000 0000
                      </p>
                    </div>
                  </div>

                  <div className="contact-info-item flex items-center w-full py-4 pl-4 sm:pl-6 gap-4 bg-white rounded-3xl">
                    <MapIcon />
                    <div>
                      <p className="text-sm text-gray-500 font-medium">
                        Visit us
                      </p>
                      <p className="text-gray-900 font-medium">
                        Lagos, Nigeria
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="contact-hero-image w-full mt-8 lg:mt-0 lg:w-[650px] lg:absolute lg:right-0 lg:top-1/2 lg:-translate-y-1/2">
              <div className="rounded-2xl overflow-hidden shadow-lg w-full">
                <img
                  src={ContactImage}
                  alt="Team member working with plants"
                  className="w-full h-64 sm:h-80 lg:h-96 object-cover"
                />
              </div>
            </div>
          </div>

          <div className="contact-form-section bg-sky-50 rounded-3xl px-6 py-8 md:py-16 md:px-32">
            <h2 className="text-3xl font-normal text-neutral-900 text-center mb-2">
              Send Us A Message
            </h2>
            <p className="text-[#8e8e8e] text-center mb-8">
              Fill out the form below and we'll respond within 24 hours
            </p>

            <form className="space-y-6">
              {/* Name and Email Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="fullName"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Full Name <small className="text-red-500">*</small>
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    placeholder="Your full name"
                    className="w-full px-4 py-3 rounded-lg border border-gray-100 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Email <small className="text-red-500">*</small>
                  </label>
                  <input
                    type="email"
                    id="email"
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 rounded-lg border border-gray-100 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="company"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Company Name <small className="text-red-500">*</small>
                  </label>
                  <input
                    type="text"
                    id="company"
                    placeholder="Your company"
                    className="w-full px-4 py-3 rounded-lg border border-gray-100 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Phone Number <small className="text-red-500">*</small>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    placeholder="+1 (555) 000-0000"
                    className="w-full px-4 py-3 rounded-lg border border-gray-100 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Your Message <small className="text-red-500">*</small>
                </label>
                <textarea
                  id="message"
                  placeholder="Tell us about your project..."
                  rows={5}
                  className="w-full px-4 py-3 rounded-lg border border-gray-100 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  required
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-[#2C73DB] hover:bg-blue-700 transition-colors"
              >
                Send Message
                <ExploreIcon />
              </Button>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ContactPage;
